import { getCountryCallingCode, parsePhoneNumber } from "react-phone-number-input";
import type { Country } from "react-phone-number-input";

/**
 * Splitting a phone number into a dialling code and a national number.
 *
 * The API wants the two parts separately (Termii's contract), while the phone
 * input produces one E.164 string. Both signup screens grew their own copy of
 * this conversion, and both copies assumed Nigeria:
 *
 *   - `normalizePhoneCode` fell back to "+234" whenever it could not read a code
 *   - `normalizeNationalPhone` did `digits.slice(-10)`, because Nigerian national
 *     numbers are 10 digits
 *
 * The second one silently corrupts numbers elsewhere. A German national number
 * can be 11 digits, so `slice(-10)` drops its leading digit and produces a
 * number that looks plausible and does not exist. Nothing surfaces the error —
 * the OTP simply never arrives.
 *
 * `libphonenumber-js` already knows every country's rules and is already a
 * direct dependency, so this uses it instead of guessing at lengths.
 */

const FALLBACK_CODE = "+234";

/** Normalizes a dialling code to "+" followed by digits. */
export function normalizePhoneCode(code?: string | null, fallback = FALLBACK_CODE): string {
  const digits = (code || "").replace(/\D/g, "");
  return digits ? `+${digits}` : fallback;
}

/**
 * Splits an E.164 number into the parts the API expects.
 *
 * Falls back to the old digit-stripping behaviour only when the value cannot be
 * parsed at all — a half-typed number, say. That path keeps its Nigerian
 * assumption because there is nothing better to go on, but a complete number
 * from any country now takes the parsed path instead.
 */
export function splitPhoneNumber(
  value?: string | null,
  fallbackCode?: string | null
): { phone_code: string; phone: string } {
  const raw = (value || "").trim();

  if (raw) {
    try {
      const parsed = parsePhoneNumber(raw.startsWith("+") ? raw : `+${raw.replace(/\D/g, "")}`);
      if (parsed?.countryCallingCode && parsed.nationalNumber) {
        return {
          phone_code: `+${parsed.countryCallingCode}`,
          phone: String(parsed.nationalNumber),
        };
      }
    } catch {
      // Unparseable, fall through to the digit-stripping path below.
    }
  }

  const phoneCode = normalizePhoneCode(fallbackCode);
  const codeDigits = phoneCode.replace(/\D/g, "");
  const digits = raw.replace(/\D/g, "");

  if (codeDigits && digits.startsWith(codeDigits) && digits.length > codeDigits.length) {
    return { phone_code: phoneCode, phone: digits.slice(codeDigits.length) };
  }

  return { phone_code: phoneCode, phone: digits };
}

/**
 * The country the phone input should open on.
 *
 * Uses the detected/declared country when there is one, so a customer in Berlin
 * is not asked to hunt for Germany in a list that opens on Nigeria. Falls back
 * to NG, which is still the overwhelming majority of signups and the right
 * default when detection says nothing.
 *
 * Returning a plain string cast rather than validating against the library's
 * Country union: an unrecognised code makes the input open on its own default,
 * which is a harmless degradation, whereas validating would need the full
 * 249-entry list shipped to the client for no real gain.
 */
export function phoneInputCountry(country?: string | null): Country {
  const normalized = country?.trim().toUpperCase();
  return (normalized && /^[A-Z]{2}$/.test(normalized) ? normalized : "NG") as Country;
}

/**
 * The dialling code a form should start with.
 *
 * Prefers whatever is already on the account, then the detected country, then
 * Nigeria. `getCountryCallingCode` throws on a country it does not recognise,
 * so the lookup is guarded — a bad detection should quietly fall back, not
 * crash a signup screen.
 */
export function defaultPhoneCodeFor(
  storedCode?: string | null,
  country?: string | null
): string {
  if (storedCode && storedCode.replace(/\D/g, "")) {
    return normalizePhoneCode(storedCode);
  }

  const normalized = country?.trim().toUpperCase();
  if (normalized && /^[A-Z]{2}$/.test(normalized)) {
    try {
      return `+${getCountryCallingCode(normalized as Country)}`;
    } catch {
      // Unrecognised country — fall through to the default.
    }
  }

  return FALLBACK_CODE;
}
